import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOrbitAnimationDetails from "../../../../hooks/useOrbitAnimationDetails";
import useThermalHeatmap from "../../../../hooks/useThermalHeatmap";
import {
  GRADIENT_STOPS,
  FaceId,
  FACE_ORDER,
  THERMAL_UPDATE_INTERVAL_MS,
  INT_ORDER,
  type IntDeckId,
  INT_LABELS,
  FACE_LABELS,
  JSON_FACE_KEY_TO_ID,
  INT_KEY_MAP,
  PLAYBACK_SPEEDS,
  LEGEND_2D_BG,
} from "../constants/thermalConstants";

export type { FaceId, IntDeckId };
export { INT_ORDER, INT_LABELS, FACE_LABELS, PLAYBACK_SPEEDS };

export interface HeatmapRow {
  time_sec: number;
  nodes: Record<string, number>;
}

interface FaceSummary {
  face: string;
  label: string;
  min: number;
  max: number;
  avg: number;
}


interface ParsedHeatmapData {
  rows: HeatmapRow[];
  nodesPerFace: number;
  internalData: Record<string, (number | number[])[]>;
  t0Epoch: number;
}

export const parseHeatmapObject = (json: any): ParsedHeatmapData | null => {
  try {
    const thermal = json?.thermal;
    if (!thermal) return null;

    const nodesPerFace: number = thermal.meta?.nodesPerFace ?? 9;
    const timeUnixSec: number[] = thermal.time?.timeEpochSec ?? [];
    const t0 = timeUnixSec[0] ?? 0;
    const faces: Record<string, number[][]> = thermal.time?.series?.faces ?? thermal.series?.faces ?? {};
    const internalData: Record<string, (number | number[])[]> = thermal.time?.series?.internal ?? thermal.series?.internal ?? {};
    const rows: HeatmapRow[] = timeUnixSec.map((t, stepIdx) => {
      const nodes: Record<string, number> = {};
      for (const [jsonKey, faceId] of Object.entries(JSON_FACE_KEY_TO_ID)) {
        const faceSteps = faces[jsonKey];
        if (!faceSteps) continue;
        const stepNodes = faceSteps[stepIdx] ?? [];
        for (let n = 0; n < nodesPerFace; n++) {
          nodes[`${faceId}_N${n + 1}`] = stepNodes[n] ?? 0;
        }
      }
      // Store internal deck temperatures — handle both scalar (current) and array (future multi-node)
      for (const { api, internal } of INT_KEY_MAP) {
        const deckSteps = internalData[api];
        if (!deckSteps) continue;
        const stepVal = deckSteps[stepIdx];
        if (stepVal === undefined || stepVal === null) continue;
        if (Array.isArray(stepVal)) {
          for (let n = 0; n < nodesPerFace; n++) nodes[`${internal}_N${n + 1}`] = (stepVal as number[])[n] ?? 0;
        } else {
          for (let n = 0; n < nodesPerFace; n++) nodes[`${internal}_N${n + 1}`] = stepVal as number;
        }
      }
      return { time_sec: t - t0, nodes };
    });
    return { rows, nodesPerFace, internalData, t0Epoch: t0 };
  } catch {
    return null;
  }
};

// ─── Typed-array series (S11: additive, not consumed by the UI yet) ──────────

// Node-major typed-array store. values[nodeIdx * length + step]; NaN marks a step with no value for that node
// (internal decks can be missing on some steps). Rows built from it omit those keys, as parseHeatmapObject does.
const ROW_CACHE_LIMIT = 16;

export class ThermalSeries {
  readonly length: number;
  readonly timeSec: Float64Array;
  readonly nodeIds: string[];
  readonly nodeIndex: Map<string, number>;
  readonly values: Float32Array;

  private rowsCache: HeatmapRow[] | null = null;
  private rowCache = new Map<number, HeatmapRow>();

  constructor(timeSec: Float64Array, nodeIds: string[], values: Float32Array) {
    this.length = timeSec.length;
    this.timeSec = timeSec;
    this.nodeIds = nodeIds;
    this.nodeIndex = new Map(nodeIds.map((id, i) => [id, i]));
    this.values = values;
  }

  time(i: number): number {
    return this.timeSec[i];
  }

  value(nodeId: string, i: number): number | undefined {
    const n = this.nodeIndex.get(nodeId);
    if (n === undefined) return undefined;
    const v = this.values[n * this.length + i];
    return Number.isNaN(v) ? undefined : v;
  }

  column(nodeId: string): Float32Array | undefined {
    const n = this.nodeIndex.get(nodeId);
    if (n === undefined) return undefined;
    return this.values.subarray(n * this.length, (n + 1) * this.length);
  }

  // One row for one step, or undefined when out of range. The same index returns the same object while it stays in a
  // small FIFO cache (or in rowsCache once toRows() has run), because consumers compare `nodes` by reference
  getRow(i: number): HeatmapRow | undefined {
    if (i < 0 || i >= this.length) return undefined;
    if (this.rowsCache) return this.rowsCache[i];
    let row = this.rowCache.get(i);
    if (!row) {
      if (this.rowCache.size >= ROW_CACHE_LIMIT) this.rowCache.delete(this.rowCache.keys().next().value as number);
      row = this.buildRow(i);
      this.rowCache.set(i, row);
    }
    return row;
  }

  toRows(): HeatmapRow[] {
    if (!this.rowsCache) {
      this.rowsCache = Array.from({ length: this.length }, (_, i) => this.buildRow(i));
    }
    return this.rowsCache;
  }

  // First index whose time is >= timeSec; returns length when none (same convention as Array.findIndex)
  indexAtOrAfter(timeSec: number): number {
    let lo = 0, hi = this.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.timeSec[mid] < timeSec) lo = mid + 1; else hi = mid;
    }
    return lo;
  }

  // Same result as findClosestIndex over the equivalent rows
  closestIndex(elapsed: number): number {
    if (this.length === 0) return 0;
    const last = this.length - 1;
    if (elapsed <= this.timeSec[0]) return 0;
    if (elapsed >= this.timeSec[last]) return last;
    let lo = 0, hi = last;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (this.timeSec[mid] <= elapsed) lo = mid; else hi = mid;
    }
    return Math.abs(this.timeSec[lo] - elapsed) <= Math.abs(this.timeSec[hi] - elapsed) ? lo : hi;
  }

  private buildRow(i: number): HeatmapRow {
    const nodes: Record<string, number> = {};
    for (let n = 0; n < this.nodeIds.length; n++) {
      const v = this.values[n * this.length + i];
      if (!Number.isNaN(v)) nodes[this.nodeIds[n]] = v;
    }
    return { time_sec: this.timeSec[i], nodes };
  }
}

const EMPTY_SERIES = new ThermalSeries(new Float64Array(0), [], new Float32Array(0));

export interface ParsedHeatmapSeries {
  series: ThermalSeries;
  nodesPerFace: number;
  internalData: Record<string, (number | number[])[]>;
  t0Epoch: number;
}

// Same input handling and node set as parseHeatmapObject, but stores values in typed arrays
export const parseHeatmapSeries = (json: any): ParsedHeatmapSeries | null => {
  try {
    const thermal = json?.thermal;
    if (!thermal) return null;

    const nodesPerFace: number = thermal.meta?.nodesPerFace ?? 9;
    const timeUnixSec: number[] = thermal.time?.timeEpochSec ?? [];
    const t0 = timeUnixSec[0] ?? 0;
    const steps = timeUnixSec.length;
    const faces: Record<string, number[][]> = thermal.time?.series?.faces ?? thermal.series?.faces ?? {};
    const internalData: Record<string, (number | number[])[]> = thermal.time?.series?.internal ?? thermal.series?.internal ?? {};

    const nodeIds: string[] = [];
    const columns: Float32Array[] = [];

    // Faces: present whenever the key exists in the payload, matching parseHeatmapObject
    for (const [jsonKey, faceId] of Object.entries(JSON_FACE_KEY_TO_ID)) {
      const faceSteps = faces[jsonKey];
      if (!faceSteps) continue;
      for (let n = 0; n < nodesPerFace; n++) {
        const col = new Float32Array(steps);
        for (let i = 0; i < steps; i++) col[i] = faceSteps[i]?.[n] ?? 0;
        nodeIds.push(`${faceId}_N${n + 1}`);
        columns.push(col);
      }
    }

    // Internal decks: scalar (replicated across nodes) or array per node; NaN where a step has no value
    for (const { api, internal } of INT_KEY_MAP) {
      const deckSteps = internalData[api];
      if (!deckSteps || !deckSteps.some((v) => v !== undefined && v !== null)) continue;
      for (let n = 0; n < nodesPerFace; n++) {
        const col = new Float32Array(steps).fill(NaN);
        for (let i = 0; i < steps; i++) {
          const stepVal = deckSteps[i];
          if (stepVal === undefined || stepVal === null) continue;
          col[i] = Array.isArray(stepVal) ? (stepVal[n] ?? 0) : (stepVal as number);
        }
        nodeIds.push(`${internal}_N${n + 1}`);
        columns.push(col);
      }
    }

    const values = new Float32Array(nodeIds.length * steps);
    columns.forEach((col, n) => values.set(col, n * steps));

    const timeSec = Float64Array.from(timeUnixSec, (t) => t - t0);
    return {
      series: new ThermalSeries(timeSec, nodeIds, values),
      nodesPerFace,
      internalData,
      t0Epoch: t0,
    };
  } catch {
    return null;
  }
};

// Min/max/avg over the columns of one face or deck. Missing values (absent node, or NaN for a deck missing on that step)
// count as 0, matching the row-based version that used `?? 0`.
const summarizeNodes = (series: ThermalSeries, prefix: string, nodesPerFace: number): Omit<FaceSummary, "face" | "label"> => {
  if (!series.length) return { min: 0, max: 0, avg: 0 };
  let min = Infinity, max = -Infinity, sum = 0;
  for (let i = 1; i <= nodesPerFace; i++) {
    const col = series.column(`${prefix}_N${i}`);
    for (let s = 0; s < series.length; s++) {
      const raw = col?.[s];
      const v = raw === undefined || Number.isNaN(raw) ? 0 : raw;
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
    }
  }
  return { min, max, avg: sum / (series.length * nodesPerFace) };
};

export const buildSummary = (series: ThermalSeries, nodesPerFace: number): FaceSummary[] =>
  FACE_ORDER.map((face) => ({ face, label: FACE_LABELS[face], ...summarizeNodes(series, face, nodesPerFace) }));

export const buildInternalSummary = (series: ThermalSeries, nodesPerFace: number): FaceSummary[] =>
  INT_ORDER.map((deck) => ({ face: deck, label: INT_LABELS[deck], ...summarizeNodes(series, deck, nodesPerFace) }));

export const formatTemp = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}°C`;

export const findClosestIndex = (rows: HeatmapRow[], elapsed: number): number => {
  if (elapsed <= rows[0].time_sec) return 0;
  if (elapsed >= rows[rows.length - 1].time_sec) return rows.length - 1;
  let lo = 0, hi = rows.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (rows[mid].time_sec <= elapsed) lo = mid; else hi = mid;
  }
  return Math.abs(rows[lo].time_sec - elapsed) <= Math.abs(rows[hi].time_sec - elapsed) ? lo : hi;
};

export const formatTime = (sec: number) => {
  const h = Math.floor(sec / 3600).toString().padStart(2, "0");
  const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export function tempToColor(temp: number, min: number, max: number): string {
  const t = max === min ? 0.5 : Math.max(0, Math.min(1, (temp - min) / (max - min)));
  const idx = Math.max(
    1,
    GRADIENT_STOPS.findIndex(([s]) => s >= t),
  );
  const [s0, c0] = GRADIENT_STOPS[idx - 1];
  const [s1, c1] = GRADIENT_STOPS[idx];
  const u = (t - s0) / (s1 - s0);
  return `rgb(${c0.map((v, i) => Math.round(v + (c1[i] - v) * u)).join(",")})`;
}

// Returns how many list items are mounted. Starts with one batch and adds one batch per animation frame, so a large
// grid of heavy children mounts in waves instead of in a single blocking commit. Resets when the items reference changes.
export const useProgressiveCount = (items: readonly unknown[], batchSize = 3): number => {
  const [progress, setProgress] = useState(() => ({ items, count: Math.min(batchSize, items.length) }));
  const count = progress.items === items ? progress.count : Math.min(batchSize, items.length);

  useEffect(() => {
    if (count >= items.length) return;
    const id = requestAnimationFrame(() => {
      setProgress({ items, count: Math.min(count + batchSize, items.length) });
    });
    return () => cancelAnimationFrame(id);
  }, [items, count, batchSize]);

  return count;
};

export const useThermalHeatMapState = () => {
  const { getViewerCurrentTime } = useOrbitAnimationDetails();
  const {
    isThermalOpen: isOpen,
    setIsThermalOpen,
    isThermalFullscreen: isFullscreen,
    setIsThermalFullscreen: setIsFullscreen,
    thermalHeatmapData,
    thermalEnvParams,
  } = useThermalHeatmap();
  const isThermalMonitor = isOpen;
  const [isMinimized, setIsMinimized] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isSynced, setIsSynced] = useState(true);
  const [isSelectedView, setIsSelectedView] = useState({ is3dView: true, isChartView: false, isMarginsView: false });
  const [heatmapRows, setHeatmapRows] = useState<HeatmapRow[]>([]);
  const [series, setSeries] = useState<ThermalSeries>(EMPTY_SERIES);
  const [nodesPerFace, setNodesPerFace] = useState(9);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepIndexOverride, setStepIndexOverride] = useState<number | null>(null);
  const [jumpVersion, setJumpVersion] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [fetchStatus, setFetchStatus] = useState<"idle" | "loading" | "unavailable" | "loaded">("idle");
  const [t0Epoch, setT0Epoch] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLastStep = stepIndex >= heatmapRows.length - 1;
  const resolvedStepIndex = stepIndexOverride !== null ? stepIndexOverride : stepIndex;

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!isPlaying || !heatmapRows.length) return;
    intervalRef.current = setInterval(() => {
      setStepIndex((prev) => {
        if (prev >= heatmapRows.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, THERMAL_UPDATE_INTERVAL_MS / playbackSpeed);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, playbackSpeed, heatmapRows.length]);

  useEffect(() => {
    if (!isThermalMonitor) return;
    if (heatmapRows.length > 0) return;
    // TODO: Remove — temporary bypass so UI renders without data
    if (!thermalHeatmapData) {
      setFetchStatus("unavailable");
      return;
    }

    let cancelled = false;
    setFetchStatus("loading");
    const id = requestAnimationFrame(() => requestAnimationFrame(() => {
      if (cancelled) return;
      const parsed = parseHeatmapSeries(thermalHeatmapData);
      if (!parsed || !parsed.series.length) {
        setFetchStatus("unavailable");
        return;
      }
      setSeries(parsed.series);
      // Rows are still needed by alerts, 3D, Margins and PDF; they are built lazily from the series (removed in S17)
      setHeatmapRows(parsed.series.toRows());
      setNodesPerFace(parsed.nodesPerFace);
      setT0Epoch(parsed.t0Epoch);
      setStepIndex(0);
      setIsPlaying(false);
      setFetchStatus("loaded");
    }));
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [isThermalMonitor, thermalHeatmapData, heatmapRows.length]);

  const handleToggleCompact = useCallback(() => {
    setIsCompact((c) => {
      if (!c) setIsFullscreen(false);
      return !c;
    });
  }, []);

  const handleToggleSync = useCallback(() => {
    setIsSynced((s) => {
      if (!s) {
        setIsPlaying(false); // stopping manual playback when re-syncing
        if (stepIndexOverride !== null) {
          setStepIndexOverride(null);
          setJumpVersion(v => v + 1);
        }
      }
      return !s;
    });
  }, [stepIndexOverride]);

  // Cesium clock → heatmap step sync
  // Only runs while the step is actually consumed (3D / Margins view, monitor open and not minimized)
  const needsStepSync = isThermalMonitor && !isMinimized && (isSelectedView.is3dView || isSelectedView.isMarginsView);
  useEffect(() => {
    if (!isSynced || !needsStepSync || !heatmapRows.length || t0Epoch === null) return;
    let rafId: number;
    let lastTick = 0;
    let lastEpoch = getViewerCurrentTime();
    const MIN_TICK_MS = 100; // ~10Hz — plenty for discrete simulation rows
    // Snap immediately when (re)starting so the view shows the current Cesium time at once
    setStepIndex(findClosestIndex(heatmapRows, lastEpoch - t0Epoch));
    const tick = (now: number) => {
      if (now - lastTick >= MIN_TICK_MS) {
        lastTick = now;
        if (!document.hidden) {
          const epoch = getViewerCurrentTime();
          if (epoch !== lastEpoch) {
            lastEpoch = epoch;
            setStepIndex(findClosestIndex(heatmapRows, epoch - t0Epoch));
          }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isSynced, needsStepSync, heatmapRows, t0Epoch, getViewerCurrentTime]);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((f) => {
      if (!f) {
        setIsMinimized(false);
        setIsCompact(false);
      }
      return !f;
    });
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (isLastStep) {
      setStepIndex(0);
      if (stepIndexOverride !== null) {
        setStepIndexOverride(null);
        setJumpVersion(v => v + 1);
      }
      setIsPlaying(true);
      return;
    }
    setIsPlaying((p) => {
      const next = !p;
      if (next && stepIndexOverride !== null) {
        setStepIndex(stepIndexOverride);
        setStepIndexOverride(null);
        setJumpVersion(v => v + 1);
      }
      return next;
    });
  }, [isLastStep, stepIndexOverride]);

  const handleCycleSpeed = useCallback(() => {
    setPlaybackSpeed((s) => PLAYBACK_SPEEDS[(PLAYBACK_SPEEDS.indexOf(s) + 1) % PLAYBACK_SPEEDS.length]);
  }, []);

  const jumpToTime = useCallback((timeSec: number) => {
    if (!heatmapRows.length) return;
    const idx = heatmapRows.findIndex(r => r.time_sec >= timeSec);
    setStepIndexOverride(idx === -1 ? heatmapRows.length - 1 : idx);
    setJumpVersion(v => v + 1);
    setIsSynced(false);
  }, [heatmapRows]);

  const resetOverride = useCallback(() => {
    setStepIndexOverride(null);
    setJumpVersion(v => v + 1);
    setIsSynced(true);
  }, []);

  const summary = useMemo(() => buildSummary(series, nodesPerFace), [series, nodesPerFace]);
  const internalSummary = useMemo(() => buildInternalSummary(series, nodesPerFace), [series, nodesPerFace]);

  const { globalMin, globalMax, summaryItems } = useMemo(() => {
    if (!heatmapRows.length) return { globalMin: null, globalMax: null, summaryItems: [] };
    let globalMin = Infinity, globalMax = -Infinity, avgSum = 0;
    let globalMinFace: FaceSummary | undefined, globalMaxFace: FaceSummary | undefined;
    for (const s of summary) {
      if (s.min < globalMin) { globalMin = s.min; globalMinFace = s; }
      if (s.max > globalMax) { globalMax = s.max; globalMaxFace = s; }
      avgSum += s.avg;
    }
    const globalAvg = summary.length ? avgSum / summary.length : 0;
    return {
      globalMin,
      globalMax,
      summaryItems: [
        { label: "Max Surface", value: formatTemp(globalMax), face: globalMaxFace?.label, color: "#FF3333" },
        { label: "Min Surface", value: formatTemp(globalMin), face: globalMinFace?.label, color: "#47B7FF" },
        { label: "Average Temp", value: formatTemp(globalAvg), color: "#CCF54E" },
      ],
    };
  }, [summary, heatmapRows.length]);

  const envParams = useMemo(() => ({
    albedo_factor: thermalEnvParams?.albedo_factor ?? null,
    blackbody_temperature: thermalEnvParams?.blackbody_temperature ?? null,
    solar_constant: thermalEnvParams?.solar_constant ?? null,
  }), [thermalEnvParams]);

  return {
    isThermalMonitor,
    setIsThermalOpen,
    isMinimized,
    setIsMinimized,
    isCompact,
    isFullscreen,
    isSynced,
    isSelectedView,
    setIsSelectedView,
    heatmapRows,
    series,
    nodesPerFace,
    stepIndex,
    stepIndexOverride,
    resolvedStepIndex,
    jumpVersion,
    isPlaying,
    playbackSpeed,
    fetchStatus,
    isLastStep,
    handleToggleCompact,
    handleToggleSync,
    handleToggleFullscreen,
    handleTogglePlay,
    handleCycleSpeed,
    jumpToTime,
    resetOverride,
    summary,
    internalSummary,
    globalMin,
    globalMax,
    summaryItems,
    envParams,
    t0Epoch,
  };
};
